import { useEffect, useMemo, useState } from "react";
import { type ColumnDef, type ColumnFiltersState } from "@tanstack/react-table";
import { PageLayout } from "@/ui/layouts";
import { Button, Card, DialogFooter, Modal } from "@/ui/atoms";
import { DataTable, EmptyState } from "@/ui/molecules";
import { useServerTable, type TServerTableQueryInput } from "@/useCases/useServerTable";
import { buildRowCount } from "@/useCases/rowCount";
import { Layers, Pencil, Plus, Trash2 } from "lucide-react";
import { cn } from "@/utils/utils";
import { SegmentValueFormModal, type TSegmentValueDraft } from "./SegmentValueFormModal";
import {
    SegmentCustomValueFormModal,
    type TSegmentCustomValueDraft,
} from "./SegmentCustomValueFormModal";
import {
    SegmentationRuleFormModal,
    type TSegmentationRuleDraft,
} from "./SegmentationRuleFormModal";

export type TSegmentValueRow = {
    id: string;
    segmentNumber: number;
    code: string;
    description: string | null;
    yardiSegmentValue: string | null;
    isActive: boolean;
};
export type TSegmentCustomValueRow = {
    id: string;
    accountCode: string;
    segmentType: number;
    code: string;
    description: string | null;
    propertyCode: string | null;
    isActive: boolean;
};
export type TSegmentationMappingRow = {
    id: string;
    accountCode: string;
    isMandatorySegment2: boolean;
    isMandatorySegment4: boolean;
    isMandatorySegment5: boolean;
    isMandatorySegment6: boolean;
    isMandatorySegment7: boolean;
    hasCustomSegment2: boolean;
    hasCustomSegment5: boolean;
    hasCustomSegment6: boolean;
    hasCustomSegment7: boolean;
    isActive: boolean;
};

export type TMasterQuery = {
    page: number;
    page_size: number;
    platform_value?: string;
    is_active?: boolean;
    sort_by?: string;
    sort_dir?: "asc" | "desc";
};
export type TCustomQuery = {
    page: number;
    page_size: number;
    account_code?: string;
    code?: string;
    property_code?: string;
    is_active?: boolean;
    sort_by?: string;
    sort_dir?: "asc" | "desc";
};
export type TRulesQuery = {
    page: number;
    page_size: number;
    account_code?: string;
    is_active?: boolean;
    is_mandatory_segment_2?: boolean;
    has_custom_segment_2?: boolean;
    sort_by?: string;
    sort_dir?: "asc" | "desc";
};

type TPageState<T> = {
    items: T[];
    total: number;
    totalUnfiltered: number;
    isLoading: boolean;
    hasError: boolean;
};

export type TViewSegmentValuesParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        permissions: () => string[];
        onLoadMasterPage: (
            segmentNumber: number,
            query: TMasterQuery,
            signal?: AbortSignal,
        ) => void;
        useMasterPage: () => TPageState<TSegmentValueRow>;
        onLoadCustomPage: (segmentType: number, query: TCustomQuery, signal?: AbortSignal) => void;
        useCustomPage: () => TPageState<TSegmentCustomValueRow>;
        onLoadRulesPage: (query: TRulesQuery, signal?: AbortSignal) => void;
        useRulesPage: () => TPageState<TSegmentationMappingRow>;
        onCreateSegmentValue: (data: {
            segment_number: number;
            code: string;
            description?: string;
            yardi_segment_value?: string;
            is_active?: boolean;
        }) => Promise<void>;
        onUpdateSegmentValue: (
            id: string,
            data: {
                code?: string;
                description?: string;
                yardi_segment_value?: string;
                is_active?: boolean;
            },
        ) => Promise<void>;
        onDeleteSegmentValue: (id: string) => Promise<void>;
        onCreateSegmentCustomValue: (data: {
            account_code: string;
            segment_type: number;
            code: string;
            description: string;
            property_code: string | null;
            is_active: boolean;
        }) => Promise<void>;
        onUpdateSegmentCustomValue: (
            id: string,
            data: { code?: string; description?: string; is_active?: boolean },
        ) => Promise<void>;
        onDeleteSegmentCustomValue: (id: string) => Promise<void>;
        onCreateSegmentationMapping: (data: TSegmentationRuleDraft) => Promise<void>;
        onUpdateSegmentationMapping: (
            id: string,
            data: Partial<TSegmentationRuleDraft>,
        ) => Promise<void>;
        onDeleteSegmentationMapping: (id: string) => Promise<void>;
        onLoadActiveMasterValues: (
            segmentNumber: number,
        ) => Promise<Array<{ code: string; description: string }>>;
    };
};

export type TViewSegmentValuesProps = Record<string, never>;

const PAGE_SIZE = 50;
const MASTER_SEGMENTS = [4, 5, 6, 7] as const;
const CUSTOM_SEGMENTS = [2, 4, 5, 6, 7] as const;

const firstString = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;
const asBool = (v: unknown): boolean | undefined => (v === true || v === false ? v : undefined);

export const createViewSegmentValues = ({ useCases }: TViewSegmentValuesParams) => {
    const ViewSegmentValues: React.FC<TViewSegmentValuesProps> = () => {
        const {
            onMount,
            onUnmount,
            permissions,
            onLoadMasterPage,
            useMasterPage,
            onLoadCustomPage,
            useCustomPage,
            onLoadRulesPage,
            useRulesPage,
            onCreateSegmentValue,
            onUpdateSegmentValue,
            onDeleteSegmentValue,
            onCreateSegmentCustomValue,
            onUpdateSegmentCustomValue,
            onDeleteSegmentCustomValue,
            onCreateSegmentationMapping,
            onUpdateSegmentationMapping,
            onDeleteSegmentationMapping,
            onLoadActiveMasterValues,
        } = useCases();

        const perms = permissions();
        const canMasterCreate = perms.includes("settings_segment_master_seg4_create");
        const canMasterEdit = perms.includes("settings_segment_master_seg4_edit");
        const canMasterDelete = perms.includes("settings_segment_master_seg4_delete");
        const canCustomCreate = perms.includes("settings_segment_custom_seg2_create");
        const canCustomEdit = perms.includes("settings_segment_custom_seg2_edit");
        const canCustomDelete = perms.includes("settings_segment_custom_seg2_delete");
        const canRuleCreate = perms.includes("settings_segment_rule_create");
        const canRuleEdit = perms.includes("settings_segment_rule_edit");
        const canRuleDelete = perms.includes("settings_segment_rule_delete");

        const [activeTab, setActiveTab] = useState<"master" | "custom" | "rules">("rules");
        const [masterSegmentTab, setMasterSegmentTab] = useState<4 | 5 | 6 | 7>(4);
        const [customSegmentType, setCustomSegmentType] = useState<number>(2);

        const [isSaving, setIsSaving] = useState(false);
        const [formError, setFormError] = useState<string | null>(null);
        const [masterCreateOpen, setMasterCreateOpen] = useState(false);
        const [masterEditRow, setMasterEditRow] = useState<TSegmentValueRow | null>(null);
        const [masterDeleteRow, setMasterDeleteRow] = useState<TSegmentValueRow | null>(null);
        const [customCreateOpen, setCustomCreateOpen] = useState(false);
        const [customCreateInitial, setCustomCreateInitial] = useState<
            TSegmentCustomValueDraft | undefined
        >(undefined);
        const [customEditRow, setCustomEditRow] = useState<TSegmentCustomValueRow | null>(null);
        const [customDeleteRow, setCustomDeleteRow] = useState<TSegmentCustomValueRow | null>(null);
        const [ruleCreateOpen, setRuleCreateOpen] = useState(false);
        const [ruleEditRow, setRuleEditRow] = useState<TSegmentationMappingRow | null>(null);
        const [ruleDeleteRow, setRuleDeleteRow] = useState<TSegmentationMappingRow | null>(null);
        const [customMasterValues, setCustomMasterValues] = useState<
            Array<{ code: string; description: string }>
        >([]);

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        // Fetch active master values when the custom CREATE modal opens for seg >= 4.
        useEffect(() => {
            if (customCreateOpen && customSegmentType >= 4) {
                void onLoadActiveMasterValues(customSegmentType)
                    .then(setCustomMasterValues)
                    .catch(() => setCustomMasterValues([]));
            } else if (!customCreateOpen) {
                setCustomMasterValues([]);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [customCreateOpen, customSegmentType]);

        // Fetch active master values when the custom EDIT modal opens for seg >= 4.
        useEffect(() => {
            const segType = customEditRow?.segmentType;
            if (customEditRow !== null && segType !== undefined && segType >= 4) {
                void onLoadActiveMasterValues(segType)
                    .then(setCustomMasterValues)
                    .catch(() => setCustomMasterValues([]));
            } else if (customEditRow === null) {
                setCustomMasterValues([]);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [customEditRow]);

        const master = useMasterPage();
        const custom = useCustomPage();
        const rules = useRulesPage();

        const masterToQuery = useMemo(
            () =>
                (input: TServerTableQueryInput): TMasterQuery => {
                    const get = (id: string) => input.columnFilters.find((f) => f.id === id)?.value;
                    const sort = input.sorting[0];
                    return {
                        page: input.page,
                        page_size: input.pageSize,
                        platform_value: firstString(get("platformValue")),
                        is_active: asBool(get("isActive")),
                        sort_by: sort?.id,
                        sort_dir: sort ? (sort.desc ? "desc" : "asc") : undefined,
                    };
                },
            [],
        );

        const masterTable = useServerTable<TMasterQuery>({
            pageSize: PAGE_SIZE,
            toQuery: masterToQuery,
            fetchData: (q, signal) => onLoadMasterPage(masterSegmentTab, q, signal),
        });

        const customToQuery = useMemo(
            () =>
                (input: TServerTableQueryInput): TCustomQuery => {
                    const get = (id: string) => input.columnFilters.find((f) => f.id === id)?.value;
                    const sort = input.sorting[0];
                    return {
                        page: input.page,
                        page_size: input.pageSize,
                        account_code: firstString(get("accountCode")),
                        code: firstString(get("code")),
                        property_code: firstString(get("propertyCode")),
                        is_active: asBool(get("isActive")),
                        sort_by: sort?.id,
                        sort_dir: sort ? (sort.desc ? "desc" : "asc") : undefined,
                    };
                },
            [],
        );

        const customTable = useServerTable<TCustomQuery>({
            pageSize: PAGE_SIZE,
            toQuery: customToQuery,
            fetchData: (q, signal) => onLoadCustomPage(customSegmentType, q, signal),
        });

        const rulesToQuery = useMemo(
            () =>
                (input: TServerTableQueryInput): TRulesQuery => {
                    const get = (id: string) => input.columnFilters.find((f) => f.id === id)?.value;
                    const sort = input.sorting[0];
                    return {
                        page: input.page,
                        page_size: input.pageSize,
                        account_code: firstString(get("accountCode")),
                        is_active: asBool(get("isActive")),
                        is_mandatory_segment_2: asBool(get("isMandatorySegment2")),
                        has_custom_segment_2: asBool(get("hasCustomSegment2")),
                        sort_by: sort?.id,
                        sort_dir: sort ? (sort.desc ? "desc" : "asc") : undefined,
                    };
                },
            [],
        );

        const rulesTable = useServerTable<TRulesQuery>({
            pageSize: PAGE_SIZE,
            toQuery: rulesToQuery,
            fetchData: onLoadRulesPage,
        });

        const runMutation = async (
            fn: () => Promise<void>,
            onSuccess: () => void,
            failMessage: string,
        ): Promise<void> => {
            setFormError(null);
            setIsSaving(true);
            try {
                await fn();
                onSuccess();
            } catch (err) {
                setFormError(err instanceof Error ? err.message : failMessage);
            } finally {
                setIsSaving(false);
            }
        };

        const submitMasterCreate = (draft: TSegmentValueDraft): void =>
            void runMutation(
                () =>
                    onCreateSegmentValue({
                        segment_number: masterSegmentTab,
                        code: draft.code,
                        description: draft.description || undefined,
                        yardi_segment_value: draft.yardi_segment_value || undefined,
                        is_active: draft.is_active,
                    }),
                () => {
                    // Create: reset to page 1 so the new row lands on a valid page.
                    setMasterCreateOpen(false);
                    masterTable.setPage(1);
                    masterTable.refetch();
                },
                "Failed to create segment value",
            );
        const submitMasterEdit = (draft: TSegmentValueDraft): void => {
            if (!masterEditRow) return;
            void runMutation(
                () =>
                    onUpdateSegmentValue(masterEditRow.id, {
                        code: draft.code,
                        description: draft.description,
                        yardi_segment_value: draft.yardi_segment_value,
                        is_active: draft.is_active,
                    }),
                () => {
                    setMasterEditRow(null);
                    masterTable.refetch();
                },
                "Failed to update segment value",
            );
        };
        const submitMasterDelete = (): void => {
            if (!masterDeleteRow) return;
            void runMutation(
                () => onDeleteSegmentValue(masterDeleteRow.id),
                () => {
                    setMasterDeleteRow(null);
                    masterTable.refetch();
                },
                "Failed to delete segment value",
            );
        };

        const submitCustomCreate = (draft: TSegmentCustomValueDraft): void =>
            void runMutation(
                () =>
                    onCreateSegmentCustomValue({
                        account_code: draft.account_code,
                        segment_type: customSegmentType,
                        code: draft.code,
                        description: draft.description,
                        property_code: draft.property_code,
                        is_active: draft.is_active,
                    }),
                () => {
                    // Create: reset to page 1 so the new row lands on a valid page.
                    setCustomCreateOpen(false);
                    customTable.setPage(1);
                    customTable.refetch();
                },
                "Failed to create custom segment value",
            );
        const submitCustomEdit = (draft: TSegmentCustomValueDraft): void => {
            if (!customEditRow) return;
            void runMutation(
                () =>
                    onUpdateSegmentCustomValue(customEditRow.id, {
                        code: draft.code,
                        description: draft.description,
                        is_active: draft.is_active,
                    }),
                () => {
                    setCustomEditRow(null);
                    customTable.refetch();
                },
                "Failed to update custom segment value",
            );
        };
        const submitCustomDelete = (): void => {
            if (!customDeleteRow) return;
            void runMutation(
                () => onDeleteSegmentCustomValue(customDeleteRow.id),
                () => {
                    setCustomDeleteRow(null);
                    customTable.refetch();
                },
                "Failed to delete custom segment value",
            );
        };

        const submitRuleCreate = (draft: TSegmentationRuleDraft): void =>
            void runMutation(
                () => onCreateSegmentationMapping(draft),
                () => {
                    // Create: reset to page 1 so the new row lands on a valid page.
                    setRuleCreateOpen(false);
                    rulesTable.setPage(1);
                    rulesTable.refetch();
                },
                "Failed to create segmentation rule",
            );
        const submitRuleEdit = (draft: TSegmentationRuleDraft): void => {
            if (!ruleEditRow) return;
            void runMutation(
                () => onUpdateSegmentationMapping(ruleEditRow.id, draft),
                () => {
                    setRuleEditRow(null);
                    rulesTable.refetch();
                },
                "Failed to update segmentation rule",
            );
        };
        const submitRuleDelete = (): void => {
            if (!ruleDeleteRow) return;
            void runMutation(
                () => onDeleteSegmentationMapping(ruleDeleteRow.id),
                () => {
                    setRuleDeleteRow(null);
                    rulesTable.refetch();
                },
                "Failed to delete segmentation rule",
            );
        };

        useEffect(() => {
            if (activeTab === "master") {
                // Switching segment changes the data source; reset to page 1 and
                // force a fetch (refetch covers the already-on-page-1 case).
                masterTable.setPage(1);
                masterTable.refetch();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [masterSegmentTab]);
        useEffect(() => {
            if (activeTab === "custom") {
                customTable.setPage(1);
                customTable.refetch();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [customSegmentType]);

        const masterColumns: ColumnDef<TSegmentValueRow>[] = useMemo(
            () => [
                {
                    accessorKey: "platformValue",
                    id: "platformValue",
                    header: "ERP Value",
                    meta: { filterType: "text", filterPlaceholder: "Search value…" },
                    cell: ({ row }) => (
                        <span className="font-mono">{row.original.yardiSegmentValue ?? "—"}</span>
                    ),
                },
                {
                    accessorKey: "isActive",
                    id: "isActive",
                    header: "Status",
                    meta: { filterType: "boolean" },
                    cell: ({ row }) =>
                        row.original.isActive ? (
                            <span className="text-xs text-emerald-700">Active</span>
                        ) : (
                            <span className="text-xs text-slate-500">Inactive</span>
                        ),
                },
                {
                    id: "actions",
                    header: "Actions",
                    enableSorting: false,
                    meta: { hidePerColumnTextSearch: true },
                    cell: ({ row }) => (
                        <div className="inline-flex justify-end gap-2 w-full">
                            {canMasterEdit && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        setFormError(null);
                                        setMasterEditRow(row.original);
                                    }}
                                >
                                    <Pencil className="h-3 w-3 mr-1" />
                                    Edit
                                </Button>
                            )}
                            {canMasterDelete && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setMasterDeleteRow(row.original)}
                                >
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    Delete
                                </Button>
                            )}
                        </div>
                    ),
                },
            ],
            [canMasterEdit, canMasterDelete],
        );

        const customColumns: ColumnDef<TSegmentCustomValueRow>[] = useMemo(
            () => [
                {
                    accessorKey: "code",
                    id: "code",
                    header: "ERP Value",
                    meta: { filterType: "text", filterPlaceholder: "Search value…" },
                    cell: ({ row }) => <span className="font-mono">{row.original.code}</span>,
                },
                {
                    accessorKey: "accountCode",
                    id: "accountCode",
                    header: "Account",
                    meta: { filterType: "text", filterPlaceholder: "Search account…" },
                    cell: ({ row }) => (
                        <span className="font-mono">{row.original.accountCode}</span>
                    ),
                },
                {
                    accessorKey: "segmentType",
                    id: "segmentType",
                    header: "Segment",
                    enableSorting: false,
                    cell: ({ row }) => <span>{row.original.segmentType}</span>,
                },
                {
                    accessorKey: "propertyCode",
                    id: "propertyCode",
                    header: "PropCode",
                    meta: { filterType: "text", filterPlaceholder: "Search propcode…" },
                    cell: ({ row }) => (
                        <span className="text-muted-foreground">
                            {row.original.propertyCode ?? "—"}
                        </span>
                    ),
                },
                {
                    accessorKey: "isActive",
                    id: "isActive",
                    header: "Status",
                    meta: { filterType: "boolean" },
                    cell: ({ row }) =>
                        row.original.isActive ? (
                            <span className="text-xs text-emerald-700">Active</span>
                        ) : (
                            <span className="text-xs text-slate-500">Inactive</span>
                        ),
                },
                {
                    id: "actions",
                    header: "Actions",
                    enableSorting: false,
                    meta: { hidePerColumnTextSearch: true },
                    cell: ({ row }) => (
                        <div className="inline-flex justify-end gap-2 w-full">
                            {canCustomEdit && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        setFormError(null);
                                        setCustomEditRow(row.original);
                                    }}
                                >
                                    <Pencil className="h-3 w-3 mr-1" />
                                    Edit
                                </Button>
                            )}
                            {canCustomDelete && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setCustomDeleteRow(row.original)}
                                >
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    Delete
                                </Button>
                            )}
                        </div>
                    ),
                },
            ],
            [canCustomEdit, canCustomDelete],
        );

        const rulesColumns: ColumnDef<TSegmentationMappingRow>[] = useMemo(
            () => [
                {
                    accessorKey: "accountCode",
                    id: "accountCode",
                    header: "Account",
                    meta: { filterType: "text", filterPlaceholder: "Search account…" },
                    cell: ({ row }) => (
                        <span className="font-mono">{row.original.accountCode}</span>
                    ),
                },
                {
                    accessorKey: "isMandatorySegment2",
                    id: "isMandatorySegment2",
                    header: "Mandatory 2",
                    enableSorting: false,
                    meta: { filterType: "boolean" },
                    cell: ({ row }) => <span>{row.original.isMandatorySegment2 ? "✓" : "—"}</span>,
                },
                {
                    id: "mandatorySegments47",
                    header: "Mandatory 4-7",
                    enableSorting: false,
                    enableColumnFilter: false,
                    meta: { hidePerColumnTextSearch: true },
                    cell: ({ row }) => (
                        <span className="whitespace-nowrap">
                            {row.original.isMandatorySegment4 ? "✓" : "—"}{" "}
                            {row.original.isMandatorySegment5 ? "✓" : "—"}{" "}
                            {row.original.isMandatorySegment6 ? "✓" : "—"}{" "}
                            {row.original.isMandatorySegment7 ? "✓" : "—"}
                        </span>
                    ),
                },
                {
                    accessorKey: "hasCustomSegment2",
                    id: "hasCustomSegment2",
                    header: "Custom 2",
                    enableSorting: false,
                    meta: { filterType: "boolean" },
                    cell: ({ row }) => <span>{row.original.hasCustomSegment2 ? "✓" : "—"}</span>,
                },
                {
                    id: "customSegments57",
                    header: "Custom 5-7",
                    enableSorting: false,
                    enableColumnFilter: false,
                    meta: { hidePerColumnTextSearch: true },
                    cell: ({ row }) => (
                        <span className="whitespace-nowrap">
                            {row.original.hasCustomSegment5 ? "✓" : "—"}{" "}
                            {row.original.hasCustomSegment6 ? "✓" : "—"}{" "}
                            {row.original.hasCustomSegment7 ? "✓" : "—"}
                        </span>
                    ),
                },
                {
                    accessorKey: "isActive",
                    id: "isActive",
                    header: "Status",
                    meta: { filterType: "boolean" },
                    cell: ({ row }) =>
                        row.original.isActive ? (
                            <span className="text-xs text-emerald-700">Active</span>
                        ) : (
                            <span className="text-xs text-slate-500">Inactive</span>
                        ),
                },
                {
                    id: "actions",
                    header: "Actions",
                    enableSorting: false,
                    meta: { hidePerColumnTextSearch: true },
                    cell: ({ row }) => (
                        <div className="inline-flex justify-end gap-2 w-full">
                            {canRuleEdit && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        setFormError(null);
                                        setRuleEditRow(row.original);
                                    }}
                                >
                                    <Pencil className="h-3 w-3 mr-1" />
                                    Edit
                                </Button>
                            )}
                            {canRuleDelete && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setRuleDeleteRow(row.original)}
                                >
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    Delete
                                </Button>
                            )}
                        </div>
                    ),
                },
            ],
            [canRuleEdit, canRuleDelete],
        );

        const anyError = master.hasError || custom.hasError || rules.hasError;
        if (anyError) {
            return (
                <PageLayout
                    title="Segment Values"
                    description="CoA list, default mapping, and custom mapping"
                    icon={<Layers className="h-6 w-6" />}
                >
                    <EmptyState
                        variant="error"
                        title="Failed to load segment data"
                        description="An error occurred while loading"
                        action={{
                            label: "Try Again",
                            onClick: () => {
                                // The error screen covers any of the three tables; retry all.
                                masterTable.refetch();
                                customTable.refetch();
                                rulesTable.refetch();
                            },
                        }}
                    />
                </PageLayout>
            );
        }

        return (
            <PageLayout
                title="Segment Values"
                description="CoA list, default mapping, and custom mapping"
                icon={<Layers className="h-6 w-6" />}
            >
                <div className="flex gap-2 border-b mb-4">
                    {(["rules", "master", "custom"] as const).map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => setActiveTab(tab)}
                            className={cn(
                                "px-4 py-2 text-sm font-medium border-b-2 -mb-px",
                                activeTab === tab
                                    ? "border-primary text-primary"
                                    : "border-transparent text-muted-foreground hover:text-foreground",
                            )}
                        >
                            {tab === "rules" && "CoA list"}
                            {tab === "master" && "Default mapping"}
                            {tab === "custom" && "Custom mapping"}
                        </button>
                    ))}
                </div>

                {activeTab === "master" && (
                    <>
                        <div className="flex items-center justify-between gap-2 mb-4">
                            <div className="flex gap-2">
                                {MASTER_SEGMENTS.map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => setMasterSegmentTab(n)}
                                        className={`px-3 py-1.5 text-sm rounded-md ${
                                            masterSegmentTab === n
                                                ? "bg-primary text-primary-foreground"
                                                : "bg-slate-100 hover:bg-slate-200"
                                        }`}
                                    >
                                        Segment {n}
                                    </button>
                                ))}
                            </div>
                            {canMasterCreate && (
                                <Button
                                    onClick={() => {
                                        setFormError(null);
                                        setMasterCreateOpen(true);
                                    }}
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    Add New
                                </Button>
                            )}
                        </div>
                        <Card className="overflow-hidden">
                            <DataTable
                                columns={masterColumns}
                                data={master.items}
                                loading={master.isLoading}
                                manualFiltering
                                manualSorting
                                sortingState={masterTable.sorting}
                                onSortingChange={masterTable.onSortingChange}
                                onColumnFiltersChange={
                                    masterTable.onColumnFiltersChange as (
                                        f: ColumnFiltersState,
                                    ) => void
                                }
                                rowNoun={{ one: "value", many: "values" }}
                                rowCount={buildRowCount({
                                    total: master.total,
                                    totalUnfiltered: master.totalUnfiltered,
                                    hasActiveFilters: masterTable.columnFilters.length > 0,
                                })}
                                pagination={{
                                    page: masterTable.page,
                                    pageSize: PAGE_SIZE,
                                    total: master.total,
                                    onPageChange: masterTable.setPage,
                                }}
                                storageKey="segment-values-master"
                                emptyMessage={`No segment values for segment ${masterSegmentTab}.`}
                            />
                        </Card>
                    </>
                )}

                {activeTab === "custom" && (
                    <>
                        <div className="flex items-center justify-between gap-2 mb-4">
                            <div className="flex gap-2">
                                {CUSTOM_SEGMENTS.map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => setCustomSegmentType(n)}
                                        className={`px-3 py-1.5 text-sm rounded-md ${
                                            customSegmentType === n
                                                ? "bg-primary text-primary-foreground"
                                                : "bg-slate-100 hover:bg-slate-200"
                                        }`}
                                    >
                                        Segment {n}
                                    </button>
                                ))}
                            </div>
                            {canCustomCreate && (
                                <Button
                                    onClick={() => {
                                        setFormError(null);
                                        // Pre-seed the new custom value from the active
                                        // account/property-code filters (parity with the
                                        // pre-server-table behaviour).
                                        const findFilter = (id: string) =>
                                            firstString(
                                                customTable.columnFilters.find((f) => f.id === id)
                                                    ?.value,
                                            );
                                        setCustomCreateInitial({
                                            account_code: findFilter("accountCode") ?? "",
                                            code: "",
                                            description: "",
                                            property_code:
                                                customSegmentType === 2
                                                    ? (findFilter("propertyCode") ?? null)
                                                    : null,
                                            is_active: true,
                                        });
                                        setCustomCreateOpen(true);
                                    }}
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    Add New
                                </Button>
                            )}
                        </div>
                        <Card className="overflow-hidden">
                            <DataTable
                                columns={customColumns}
                                data={custom.items}
                                loading={custom.isLoading}
                                manualFiltering
                                manualSorting
                                sortingState={customTable.sorting}
                                onSortingChange={customTable.onSortingChange}
                                onColumnFiltersChange={
                                    customTable.onColumnFiltersChange as (
                                        f: ColumnFiltersState,
                                    ) => void
                                }
                                rowNoun={{ one: "value", many: "values" }}
                                rowCount={buildRowCount({
                                    total: custom.total,
                                    totalUnfiltered: custom.totalUnfiltered,
                                    hasActiveFilters: customTable.columnFilters.length > 0,
                                })}
                                pagination={{
                                    page: customTable.page,
                                    pageSize: PAGE_SIZE,
                                    total: custom.total,
                                    onPageChange: customTable.setPage,
                                }}
                                storageKey="segment-values-custom"
                                emptyMessage="No custom values match the current filters."
                            />
                        </Card>
                    </>
                )}

                {activeTab === "rules" && (
                    <>
                        {canRuleCreate && (
                            <div className="flex justify-end mb-4">
                                <Button
                                    onClick={() => {
                                        setFormError(null);
                                        setRuleCreateOpen(true);
                                    }}
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    Add New
                                </Button>
                            </div>
                        )}
                        <Card className="overflow-hidden">
                            <DataTable
                                columns={rulesColumns}
                                data={rules.items}
                                loading={rules.isLoading}
                                manualFiltering
                                manualSorting
                                sortingState={rulesTable.sorting}
                                onSortingChange={rulesTable.onSortingChange}
                                onColumnFiltersChange={
                                    rulesTable.onColumnFiltersChange as (
                                        f: ColumnFiltersState,
                                    ) => void
                                }
                                rowNoun={{ one: "rule", many: "rules" }}
                                rowCount={buildRowCount({
                                    total: rules.total,
                                    totalUnfiltered: rules.totalUnfiltered,
                                    hasActiveFilters: rulesTable.columnFilters.length > 0,
                                })}
                                pagination={{
                                    page: rulesTable.page,
                                    pageSize: PAGE_SIZE,
                                    total: rules.total,
                                    onPageChange: rulesTable.setPage,
                                }}
                                storageKey="segment-values-rules"
                                emptyMessage="No segmentation rules match the current filters."
                            />
                        </Card>
                    </>
                )}

                {/* Master tab create/edit/delete modals */}
                <SegmentValueFormModal
                    open={masterCreateOpen}
                    mode="create"
                    segmentNumber={masterSegmentTab}
                    isSaving={isSaving}
                    error={formError}
                    onSubmit={submitMasterCreate}
                    onClose={() => setMasterCreateOpen(false)}
                />
                <SegmentValueFormModal
                    open={masterEditRow !== null}
                    mode="edit"
                    segmentNumber={masterSegmentTab}
                    initial={
                        masterEditRow
                            ? {
                                  code: masterEditRow.code,
                                  description: masterEditRow.description ?? "",
                                  yardi_segment_value: masterEditRow.yardiSegmentValue ?? "",
                                  is_active: masterEditRow.isActive,
                              }
                            : undefined
                    }
                    isSaving={isSaving}
                    error={formError}
                    onSubmit={submitMasterEdit}
                    onClose={() => setMasterEditRow(null)}
                />
                <Modal
                    open={masterDeleteRow !== null}
                    onOpenChange={(next) => !next && setMasterDeleteRow(null)}
                    title="Delete Segment Value"
                    description={
                        masterDeleteRow
                            ? `Delete ${masterDeleteRow.yardiSegmentValue ?? masterDeleteRow.code}? This cannot be undone.`
                            : undefined
                    }
                >
                    <DialogFooter className="gap-2 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setMasterDeleteRow(null)}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={submitMasterDelete}
                            disabled={isSaving}
                        >
                            {isSaving ? "Deleting..." : "Delete"}
                        </Button>
                    </DialogFooter>
                </Modal>

                {/* Custom tab create/edit/delete modals */}
                <SegmentCustomValueFormModal
                    open={customCreateOpen}
                    mode="create"
                    segmentType={customSegmentType}
                    masterValues={customMasterValues}
                    initial={customCreateInitial}
                    isSaving={isSaving}
                    error={formError}
                    onSubmit={submitCustomCreate}
                    onClose={() => setCustomCreateOpen(false)}
                />
                <SegmentCustomValueFormModal
                    open={customEditRow !== null}
                    mode="edit"
                    segmentType={customEditRow?.segmentType ?? customSegmentType}
                    masterValues={customMasterValues}
                    initial={
                        customEditRow
                            ? {
                                  account_code: customEditRow.accountCode,
                                  code: customEditRow.code,
                                  description: customEditRow.description ?? "",
                                  property_code: customEditRow.propertyCode,
                                  is_active: customEditRow.isActive,
                              }
                            : undefined
                    }
                    isSaving={isSaving}
                    error={formError}
                    onSubmit={submitCustomEdit}
                    onClose={() => setCustomEditRow(null)}
                />
                <Modal
                    open={customDeleteRow !== null}
                    onOpenChange={(next) => !next && setCustomDeleteRow(null)}
                    title="Delete Custom Value"
                    description={
                        customDeleteRow
                            ? `Delete ${customDeleteRow.code}? This cannot be undone.`
                            : undefined
                    }
                >
                    <DialogFooter className="gap-2 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCustomDeleteRow(null)}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={submitCustomDelete}
                            disabled={isSaving}
                        >
                            {isSaving ? "Deleting..." : "Delete"}
                        </Button>
                    </DialogFooter>
                </Modal>

                {/* Rules tab create/edit/delete modals */}
                <SegmentationRuleFormModal
                    open={ruleCreateOpen}
                    mode="create"
                    isSaving={isSaving}
                    error={formError}
                    onSubmit={submitRuleCreate}
                    onClose={() => setRuleCreateOpen(false)}
                />
                <SegmentationRuleFormModal
                    open={ruleEditRow !== null}
                    mode="edit"
                    initial={
                        ruleEditRow
                            ? {
                                  account_code: ruleEditRow.accountCode,
                                  is_mandatory_segment_2: ruleEditRow.isMandatorySegment2,
                                  is_mandatory_segment_4: ruleEditRow.isMandatorySegment4,
                                  is_mandatory_segment_5: ruleEditRow.isMandatorySegment5,
                                  is_mandatory_segment_6: ruleEditRow.isMandatorySegment6,
                                  is_mandatory_segment_7: ruleEditRow.isMandatorySegment7,
                                  has_custom_segment_2: ruleEditRow.hasCustomSegment2,
                                  has_custom_segment_5: ruleEditRow.hasCustomSegment5,
                                  has_custom_segment_6: ruleEditRow.hasCustomSegment6,
                                  has_custom_segment_7: ruleEditRow.hasCustomSegment7,
                                  is_active: ruleEditRow.isActive,
                              }
                            : undefined
                    }
                    isSaving={isSaving}
                    error={formError}
                    onSubmit={submitRuleEdit}
                    onClose={() => setRuleEditRow(null)}
                />
                <Modal
                    open={ruleDeleteRow !== null}
                    onOpenChange={(next) => !next && setRuleDeleteRow(null)}
                    title="Delete Segmentation Rule"
                    description={
                        ruleDeleteRow
                            ? `Delete the rule for ${ruleDeleteRow.accountCode}? This cannot be undone.`
                            : undefined
                    }
                >
                    <DialogFooter className="gap-2 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setRuleDeleteRow(null)}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={submitRuleDelete}
                            disabled={isSaving}
                        >
                            {isSaving ? "Deleting..." : "Delete"}
                        </Button>
                    </DialogFooter>
                </Modal>
            </PageLayout>
        );
    };

    return ViewSegmentValues;
};
